import { Box, Chip, Stack, Typography } from '@mui/material';
import type { Ticket } from '../api';
import type { WaveGroup } from '../dependencyLayout';

/**
 * The same ordering as the graph, with no lines in it.
 *
 * A wave is a SET: the drawn tickets sharing one `chainDepth`, which is why it has its own
 * word — depth is the per-ticket number the API and the ticket cards use, and a wave is a
 * group of tickets at that depth. Reading the order stops needing anyone to trace an edge.
 *
 * ⚠️ Wave 0 is NOT the `Ready to start` list above it and must not be captioned as though
 * it were. That list is every unstarted in-scope ticket with no unfinished blocker,
 * including the many that no link touches; a wave holds only DRAWN tickets, and includes
 * finished ones. The two answer "what can I pick up" over different populations, so the
 * captions here describe depth — what stands in front of these tickets — and leave the
 * startability claim to the fact that actually publishes it.
 */

const WAVE_MEANING = [
  'Nothing unfinished in front of them',
  'One round of unfinished work in front of them',
  'Two rounds in front of them',
  'Three rounds in front of them',
  'Four rounds in front of them',
];

const waveMeaning = (depth: number) => WAVE_MEANING[depth] ?? `${depth} rounds in front of them`;

function statusColour(status: string | undefined) {
  if (status === 'Done') return 'success' as const;
  if (status === 'In progress') return 'primary' as const;
  return 'default' as const;
}

/** Closed work is shown struck through — present, and plainly not outstanding. */
const chipTextStyle = (status: string | undefined) =>
  status === 'Closed' ? { textDecoration: 'line-through' as const, opacity: 0.7 } : undefined;

export function DependencyWaves({
  waves,
  tickets,
  external,
  onOpen,
}: {
  waves: WaveGroup[];
  /** Every ticket on the board — a blocker is often in another epic. */
  tickets: Ticket[];
  external: Set<number>;
  onOpen: (ref: number) => void;
}) {
  const byRef = new Map(tickets.map((ticket) => [ticket.ref, ticket]));

  return (
    // Many waves scroll sideways inside the panel rather than stretching the page.
    <Box sx={{ overflowX: 'auto', py: 1 }}>
      <Stack direction="row" spacing={2} alignItems="flex-start" sx={{ minWidth: 'min-content' }}>
        {waves.map((wave) => (
          <Box key={wave.depth ?? 'unscoped'} sx={{ width: 200, flex: 'none' }}>
            <Typography
              variant="caption"
              sx={{ display: 'block', fontWeight: 600, letterSpacing: '.04em' }}
            >
              {wave.depth === null ? 'Other epics' : `Wave ${wave.depth}`} &middot; {wave.refs.length}
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 1, minHeight: 40 }}
            >
              {/*
                `chainDepth` is computed over this epic's tickets, so a blocker filed
                elsewhere has no wave. Saying so is better than inventing a number the
                MCP would not agree with, or hiding a ticket the other views draw.
              */}
              {wave.depth === null
                ? 'Outside this epic, so no depth is computed for them'
                : waveMeaning(wave.depth)}
            </Typography>
            <Stack spacing={0.75}>
              {wave.refs.map((ref) => (
                <Chip
                  key={ref}
                  size="small"
                  label={`DWLF-${ref}`}
                  title={byRef.get(ref)?.name ?? ''}
                  color={statusColour(byRef.get(ref)?.status)}
                  variant={external.has(ref) ? 'outlined' : 'filled'}
                  onClick={() => onOpen(ref)}
                  sx={{
                    justifyContent: 'flex-start',
                    fontVariantNumeric: 'tabular-nums',
                    ...chipTextStyle(byRef.get(ref)?.status),
                  }}
                />
              ))}
            </Stack>
          </Box>
        ))}
      </Stack>
    </Box>
  );
}
